import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocalStorage, useWindowSize } from 'react-use';

interface ResizablePanelOptions {
  /** Key the width is remembered under across sessions. */
  storageKey: string;
  defaultWidth: number;
  minWidth: number;
  maxWidth: number;
  /** Space always left for the neighbouring panel so it can't be squeezed out. */
  reservedWidth?: number;
}

/**
 * Drag-to-resize state for a horizontal split (fixed-width panel + flexible pane).
 * Spread `separatorProps` onto the drag handle and apply `width` to the panel.
 */
export function useResizablePanel({
  storageKey,
  defaultWidth,
  minWidth,
  maxWidth,
  reservedWidth = 0,
}: ResizablePanelOptions) {
  const [storedWidth, setStoredWidth] = useLocalStorage(
    storageKey,
    defaultWidth,
  );
  const { width: windowWidth } = useWindowSize();
  const [isResizing, setIsResizing] = useState(false);
  const dragOrigin = useRef<{ pointerX: number; width: number } | null>(null);

  // Clamped on render so a narrow window can never hide the neighbouring pane.
  const upperBound = Math.max(
    minWidth,
    Math.min(maxWidth, windowWidth - reservedWidth),
  );
  const width = Math.min(Math.max(storedWidth ?? defaultWidth, minWidth), upperBound);

  const resizeTo = useCallback(
    (value: number) => setStoredWidth(Math.round(value)),
    [setStoredWidth],
  );

  // Keep the resize cursor while dragging, even outside the handle.
  useEffect(() => {
    if (!isResizing) return;

    const { body } = document;
    const previousCursor = body.style.cursor;
    const previousUserSelect = body.style.userSelect;
    body.style.cursor = 'col-resize';
    body.style.userSelect = 'none';

    return () => {
      body.style.cursor = previousCursor;
      body.style.userSelect = previousUserSelect;
    };
  }, [isResizing]);

  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      if (event.button !== 0) return;
      event.preventDefault();
      dragOrigin.current = { pointerX: event.clientX, width };
      // Pointer capture keeps move/up events on the handle while dragging.
      event.currentTarget.setPointerCapture(event.pointerId);
      setIsResizing(true);
    },
    [width],
  );

  const handlePointerMove = useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      const origin = dragOrigin.current;
      if (!origin) return;
      resizeTo(origin.width + (event.clientX - origin.pointerX));
    },
    [resizeTo],
  );

  const handlePointerUp = useCallback(() => {
    dragOrigin.current = null;
    setIsResizing(false);
  }, []);

  return {
    width,
    isResizing,
    reset: useCallback(() => resizeTo(defaultWidth), [defaultWidth, resizeTo]),
    separatorProps: {
      role: 'separator',
      'aria-orientation': 'vertical',
      'aria-valuenow': width,
      'aria-valuemin': minWidth,
      'aria-valuemax': maxWidth,
      onPointerDown: handlePointerDown,
      onPointerMove: handlePointerMove,
      onPointerUp: handlePointerUp,
      onPointerCancel: handlePointerUp,
      onDoubleClick: () => resizeTo(defaultWidth),
    } as const,
  };
}

export default useResizablePanel;
